package dev.wyns.ventrova.ui.map

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.onClick
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.status_closed_short
import dev.wyns.ventrova.ui.components.LevelGlyph
import dev.wyns.ventrova.ui.components.MenuRing
import dev.wyns.ventrova.ui.components.menuShare
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.theme.LocalVentrovaFonts
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.filledIcon
import dev.wyns.ventrova.ui.theme.palette
import org.jetbrains.compose.resources.stringResource

/** Per-platform differences of the marker label. */
data class MarkerStyle(
    val labelHeight: Dp,
    val labelCornerRadius: Dp,
    val labelFontSize: TextUnit,
    val labelPadding: Dp,
    val selectedScale: Float,
) {
    companion object {
        val Ios = MarkerStyle(labelHeight = 18.dp, labelCornerRadius = 9.dp, labelFontSize = 10.5.sp, labelPadding = 6.dp, selectedScale = 1.16f)
        val Android = MarkerStyle(labelHeight = 18.dp, labelCornerRadius = 6.dp, labelFontSize = 10.5.sp, labelPadding = 6.dp, selectedScale = 1.16f)
        val Web = MarkerStyle(labelHeight = 20.dp, labelCornerRadius = 10.dp, labelFontSize = 11.sp, labelPadding = 7.dp, selectedScale = 1.1f)
    }
}

/**
 * The design's "ring" marker: a category icon in a ring, a level glyph in the corner and a label
 * underneath with the name (when [showName]) and a closed badge.
 */
@Composable
fun PlaceMarker(
    item: PlaceItem,
    selected: Boolean,
    showName: Boolean,
    style: MarkerStyle,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val place = item.place
    val closed = item.openStatus?.isOpen == false
    val scale by animateFloatAsState(
        targetValue = if (selected) style.selectedScale else 1f,
        animationSpec = spring(dampingRatio = 0.55f, stiffness = Spring.StiffnessMediumLow),
    )
    val iconColor = when {
        selected -> Color.White
        closed -> VentrovaColors.ClosedMarker
        else -> place.veganLevel.palette.color
    }
    val surface = if (selected) VentrovaColors.Ink else Color.White
    val currentOnClick by rememberUpdatedState(onClick)

    Column(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .markerTap { currentOnClick() },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(3.dp),
    ) {
        Box(Modifier.size(44.dp)) {
            MenuRing(
                share = place.menuShare,
                width = 4.dp,
                alpha = if (closed) 0.5f else 1f,
                // Open where the level glyph sits (top right), so it never hides part of the ring.
                gap = 60f,
                gapCenter = -45f,
                modifier = Modifier.size(44.dp).shadow(6.dp, CircleShape, ambientColor = MarkerShadow, spotColor = MarkerShadow).background(Color.White, CircleShape),
            )
            Box(
                Modifier.align(Alignment.Center).size(36.dp).clip(CircleShape).background(surface),
                contentAlignment = Alignment.Center,
            ) {
                VIcon(place.category.filledIcon, size = 19.dp, tint = iconColor)
            }
            LevelGlyph(
                level = place.veganLevel,
                size = 13.dp,
                borderWidth = 2.dp,
                haloWidth = 2.dp,
                modifier = Modifier.align(Alignment.TopEnd).offset(x = 3.dp, y = (-3).dp),
            )
        }
        if (showName || closed) {
            Row(
                Modifier
                    .height(style.labelHeight)
                    .shadow(2.dp, RoundedCornerShape(style.labelCornerRadius))
                    .background(surface, RoundedCornerShape(style.labelCornerRadius))
                    .padding(horizontal = style.labelPadding),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(3.dp),
            ) {
                val textStyle = TextStyle(
                    fontFamily = LocalVentrovaFonts.current.body,
                    fontSize = style.labelFontSize,
                    fontWeight = FontWeight.Bold,
                    color = if (selected) Color.White else VentrovaColors.Ink,
                )
                if (showName) {
                    Text(place.name, style = textStyle, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.widthIn(max = 160.dp))
                }
                if (closed) {
                    Text(
                        (if (showName) "· " else "") + stringResource(Res.string.status_closed_short),
                        style = textStyle.copy(color = if (selected) Color.White.copy(alpha = 0.7f) else VentrovaColors.Muted),
                        maxLines = 1,
                    )
                }
            }
        }
    }
}

private val MarkerShadow = Color(0x591C1F1A)

/**
 * A tap that leaves the press to the map, so a drag that starts on a marker still pans it.
 * `clickable` consumes the press, and the map gives up a gesture whose press was consumed by its
 * content. Only a release close to the press is consumed, which also stops the map's own click.
 */
private fun Modifier.markerTap(onTap: () -> Unit): Modifier =
    semantics {
        role = Role.Button
        onClick { onTap(); true }
    }.pointerInput(Unit) {
        awaitEachGesture {
            val down = awaitFirstDown(requireUnconsumed = false)
            val slop = if (down.type == PointerType.Mouse) 3.dp.toPx() else viewConfiguration.touchSlop
            var isTap = true
            while (true) {
                val event = awaitPointerEvent()
                val change = event.changes.firstOrNull { it.id == down.id } ?: break
                if (event.changes.count { it.pressed } > 1 || (change.position - down.position).getDistance() > slop) {
                    isTap = false
                }
                if (!change.pressed) {
                    if (isTap && !change.isConsumed) {
                        change.consume()
                        onTap()
                    }
                    break
                }
            }
        }
    }
